@use "styles/abstracts/variables" as v;
@use "styles/abstracts/mixins/typography" as t;
@use "styles/abstracts/mixins/common" as m;

.modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  max-height: min(1020px, calc(100dvh - 32px));
  padding: 0;
  background: v.$palette-white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px 0 rgba(58, 112, 191, 0.1);

  @include m.media(800) {
    max-width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
  }

  @include m.media(480) {
    max-width: calc(100vw - 16px);
    max-height: calc(100dvh - 16px);
    border-radius: 12px;
  }
}

.header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  min-height: 60px;
  box-sizing: border-box;
  flex-shrink: 0;

  @include m.media(768) {
    padding: 12px 16px;
    min-height: 52px;
  }
}

.title {
  flex: 1;
  margin: 0;
  min-width: 0;
  @include t.text(18px, 28px, 600);
  color: v.$palette-gray-900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include m.media(480) {
    @include t.text(16px, 24px, 600);
  }
}

.closeBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  img {
    display: block;
    width: 20px;
    height: 20px;
  }
}

.viewport {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 580px;
  background: #f3f5f9;
  overflow: hidden;
  flex: 1 1 auto;
}

// Fixed render resolution — matches CANVAS_WIDTH/CANVAS_HEIGHT in
// RenderModal.tsx so the WebGL canvas never rescales with its container.
// On narrow screens it's scaled down visually (transform) rather than
// re-rendered at a different resolution, so framing stays identical.
.canvasHost {
  flex-shrink: 0;
  width: 860px;
  height: 580px;

  :global(canvas) {
    width: 860px !important;
    height: 580px !important;
    max-width: none;
    display: block;
  }

  :global(.canvas) {
    width: 860px;
    height: 580px;
  }

  @include m.media(900) {
    transform: scale(0.82);
  }

  @include m.media(700) {
    transform: scale(0.6);
  }

  @include m.media(480) {
    transform: scale(0.42);
  }
}

.downloadBtn {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  max-width: calc(100% - 200px);
  padding: 0 12px;
  border: none;
  border-radius: 6px;
  background: v.$palette-white;
  box-shadow: v.$shadow2;
  cursor: pointer;
  @include t.text(12px, 20px, 600);
  color: v.$palette-orange-2;
  white-space: nowrap;
  font-family: v.$primary-font;

  img {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (pointer: fine) {
    &:hover {
      color: v.$palette-orange-2;
      background: v.$palette-orange-4;
    }
  }

  @include m.media(768) {
    top: 12px;
    left: 12px;
  }

  @include m.media(480) {
    max-width: calc(50% - 16px);
    height: 36px;
    padding: 0 10px;
  }
}

.statsCard {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 0;
  max-width: calc(50% - 16px);
  padding: 8px 16px;
  background: v.$palette-white;
  border-radius: 16px;
  box-shadow: v.$shadow2;

  @include m.media(768) {
    top: 12px;
    right: 12px;
    padding: 8px 12px;
    border-radius: 12px;
  }

  @include m.media(480) {
    padding: 6px 10px;
  }
}

.statsRow {
  display: flex;
  gap: 4px;
}

.statsLabel,
.statsValue {
  width: 140px;
  margin: 0;
  @include t.text(14px, 22px, 600);
  color: #15354e;

  @include m.media(768) {
    width: auto;
    min-width: 0;
    flex: 1;
  }

  @include m.media(480) {
    @include t.text(11px, 16px, 600);
  }
}

.statsValue {
  font-weight: 400;
}

.controls {
  position: absolute;
  left: 24px;
  right: 24px;
  bottom: 20px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background: v.$palette-white;
  border-radius: 16px;
  box-shadow: v.$shadow2;

  @include m.media(768) {
    left: 12px;
    right: 12px;
    bottom: 12px;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 12px;
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    grid-template-areas:
      "group progress count pdf"
      "group progress count pdf";
    align-items: center;
  }

  @include m.media(560) {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "group count pdf"
      "progress progress progress";
    row-gap: 10px;
  }
}

.controlGroup {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @include m.media(768) {
    grid-area: group;
  }
}

.controlBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  img {
    display: block;
    width: 40px;
    height: 40px;
  }

  @include m.media(480) {
    width: 36px;
    height: 36px;

    img {
      width: 36px;
      height: 36px;
    }
  }
}

.progress {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 4px;
  border-radius: 4px;
  background: v.$palette-dirty-40;
  overflow: visible;

  @include m.media(768) {
    grid-area: progress;
  }
}

.progressFill {
  height: 100%;
  border-radius: 4px;
  background: v.$palette-orange-2;
  transition: width 0.3s ease;
}

.progressThumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: v.$palette-orange-2;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.stepCount {
  flex-shrink: 0;
  margin: 0;
  @include t.text(14px, 22px, 400);
  color: v.$palette-gray-900;
  white-space: nowrap;

  @include m.media(768) {
    grid-area: count;
  }

  @include m.media(480) {
    @include t.text(12px, 18px, 400);
  }
}

.controls button.stepPdfBtn {
  flex-shrink: 0;
  padding: 6px 16px !important;
  color: v.$palette-white;
  background-color: v.$palette-orange-2;
  @include t.text(12px, 20px, 600);

  @media (pointer: fine) {
    &:hover {
      color: v.$palette-white;
      background-color: v.$palette-orange-3;
    }
  }

  img {
    display: block;
    width: 16px;
    height: 16px;
  }

  @include m.media(768) {
    grid-area: pdf;
  }

  @include m.media(480) {
    padding: 8px 12px !important;
  }
}

.legend {
  padding: 0;
  flex-shrink: 0;
  max-height: 30%;
  overflow: auto;
}

.loadingOverlay {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(2px);
  box-sizing: border-box;
}
